import { gql, useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { logWarning } from '@/imports/logging/ClientLogger';

import {
  useDownloadUserCsvErrors_getErrorsForUserCsvUpload,
  useDownloadUserCsvErrors_getErrorsForUserCsvUploadVariables,
} from './graphql-types/useDownloadUserCsvErrors_getErrorsForUserCsvUpload';

import { useDownloadErrorFile } from './useDownloadErrorFile';

const USER_CSV_UPLOAD_ERROR = gql`
  query useDownloadUserCsvErrors_getErrorsForUserCsvUpload(
    $input: GetErrorsForUserCsvUploadInput!
  ) {
    getErrorsForUserCsvUpload(input: $input) {
      errorFileUrl
    }
  }
`;

export const useDownloadUserCsvErrors = (options: {
  validRoutes: string[];
}): void => {
  const router = useRouter();
  const userCsvUploadId = router.query.userCsvUploadId;

  const isValidRoute = options.validRoutes.reduce(
    (prev, route) => prev || router.pathname.endsWith(route),
    false
  );

  const [getErrorsForUserCsvUpload, { data, called }] = useLazyQuery<
    useDownloadUserCsvErrors_getErrorsForUserCsvUpload,
    useDownloadUserCsvErrors_getErrorsForUserCsvUploadVariables
  >(USER_CSV_UPLOAD_ERROR);

  React.useEffect(() => {
    if (!router.isReady || !userCsvUploadId || called || !isValidRoute) {
      return;
    }

    getErrorsForUserCsvUpload({
      variables: {
        input: { data: { userCsvUploadId: userCsvUploadId as string } },
      },
    });
  }, [
    router,
    called,
    userCsvUploadId,
    getErrorsForUserCsvUpload,
    isValidRoute,
  ]);

  React.useEffect(() => {
    if (data && !data.getErrorsForUserCsvUpload.errorFileUrl) {
      logWarning(
        `Could not find error file for userCsvUpload: ${userCsvUploadId}`
      );
    }
  }, [data, userCsvUploadId]);

  useDownloadErrorFile({
    fileUrl: data?.getErrorsForUserCsvUpload.errorFileUrl ?? undefined,
    shouldDownload: !!userCsvUploadId,
  });
};
